/**
 * شاشة «الرحلات» — مين واقف وعند مين.
 *
 * 🔴 **ليه الشاشة دي أهم من شكلها:** السيستم كله مبني على الرحلة، والرحلة
 *    الواقفة كانت **غير مرئية لأي حد**. الفريق بيشوف الطابور بيتراكم ومش
 *    عارف السبب، ومدير الأكاديمية مالوش أي صورة. محاكاة حيّة على الإنتاج
 *    أثبتت إن رحلة بتقف عند خطوة ٤ من ٨ **ومحدش يعرف**.
 *
 * 🔑 فلتر **«الواقفة»** هو السؤال اللي الشاشة موجودة عشانه — مش فلتر إضافي.
 *    وتعريف «واقفة» مش مخترع هنا: السيرفر بيحسبه بنفس تعريف لوحة المدير
 *    (`journeys.stalled_filter` + `settings.journey_stall_hours`)، فالرقم
 *    اللي المدير بيشوفه في لوحته هو نفسه اللي في الشاشة دي.
 *
 * ⚠️ **العزل من السيرفر** — `scope_filter` بيتحقن في كل استعلام، فالعضو
 *    بيشوف رحلات أقسامه بس. الشاشة **ما بتفلترش** وما بتفترضش.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoResultsState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { Dash } from "@/components/RegistryBits";
import { fmtNum } from "@/lib/format";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useRouter } from "@/lib/router";
import {
  completeStep,
  pauseJourney,
  resumeJourney,
  getJourney,
  listJourneys,
  nudgeStep,
  type JourneyListMeta,
  type JourneyRow,
} from "@/api/journeys";
import "@/styles/registry.css";

const PER_PAGE = 12;
const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 350;

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; rows: JourneyRow[]; meta: JourneyListMeta }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

/** الشرايح — «الواقفة» أول واحدة عن قصد: هي سبب وجود الشاشة. */
const SEGMENTS = [
  { key: "stuck", label: "الواقفة" },
  { key: "active", label: "الشغّالة" },
  { key: "all", label: "الكل" },
] as const;
type Segment = (typeof SEGMENTS)[number]["key"];

export function journeyIdFromPath(path: string): string | null {
  const m = /^\/journeys\/([^/?#]+)/.exec(path);
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

export function Journeys() {
  const { path } = useRouter();
  const id = journeyIdFromPath(path);
  if (id) return <JourneyFile journeyId={id} />;
  return <JourneysList />;
}

/* ══════════════════════════ القايمة ══════════════════════════ */

function JourneysList() {
  const { navigate } = useRouter();
  const [segment, setSegment] = useState<Segment>("stuck");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [state, setState] = useState<PageState>({ kind: "loading" });

  const debounce = useRef<number | null>(null);
  useEffect(() => {
    const trimmed = searchInput.trim();
    const next = trimmed.length >= MIN_SEARCH ? trimmed : "";
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      setQuery((cur) => (cur === next ? cur : next));
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [searchInput]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const { rows, meta } = await listJourneys({
        page,
        perPage: PER_PAGE,
        q: query || undefined,
        stuck: segment === "stuck",
        // ⚠️ «الشغّالة» = الحالة، و«الواقفة» = تعريف السيرفر (بيشمل الحالة)
        ...(segment === "active" ? { status: "active" } : {}),
      });
      setState({ kind: "ready", rows, meta });
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت
      console.error("[journeys] تحميل الرحلات فشل", err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, [segment, page, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = state.kind === "ready" ? state.rows : [];
  const meta = state.kind === "ready" ? state.meta : null;

  return (
    <Shell
      title="الرحلات"
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: "ابحث باسم صاحب الرحلة…",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {SEGMENTS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`filt${segment === s.key ? " on" : ""}`}
            onClick={() => {
              setSegment(s.key);
              setPage(1);
            }}
          >
            {s.label}
          </button>
        ))}
        {meta && segment === "stuck" ? (
          <span className="t-meta">
            «واقفة» = عدّت <span className="num">{fmtNum(meta.stallHours)}</span> ساعة
            بلا حركة — نفس تعريف لوحة المدير
          </span>
        ) : null}
      </div>

      <div className="reg-card">
        <div className="reg-head">
          <span className="sec-title">
            {meta ? (
              <>
                <span className="num">{fmtNum(meta.total)}</span> رحلة
              </>
            ) : (
              "الرحلات"
            )}
            {query ? ` · بحث عن «${query}»` : ""}
          </span>
        </div>

        {state.kind === "loading" ? <LoadingState rows={5} /> : null}
        {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
        {state.kind === "error" ? (
          <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        ) : null}

        {/* ⛔ حالات فاضية حقيقية — مش شاشة بيضا */}
        {state.kind === "ready" && rows.length === 0 && query ? (
          <NoResultsState query={query} onClear={() => setSearchInput("")} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment === "stuck" ? (
          <EmptyState
            title="مفيش ولا رحلة واقفة 🌿"
            hint="كل الرحلات ماشية في وقتها — ده خبر كويس"
            action={{ label: "شوف الكل", onClick: () => setSegment("all") }}
          />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment !== "stuck" ? (
          <EmptyState
            title="لسه مفيش رحلات"
            hint="الرحلة بتتفتح لوحدها أول ما طلب تسجيل يوصل من الموقع"
          />
        ) : null}

        {rows.length > 0 ? (
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>صاحب الرحلة</th>
                  <th>النوع</th>
                  <th>واقفة عند</th>
                  <th>القسم المسؤول</th>
                  <th>من إمتى</th>
                  <th>التقدّم</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((j) => (
                  <tr
                    key={j.id}
                    onClick={() => navigate(`/journeys/${j.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <bdi>{j.subject_label_ar}</bdi>
                    </td>
                    <td className="t-desc">{j.template_label_ar || <Dash />}</td>
                    <td>
                      {j.current_step_no ? (
                        <>
                          <span className="num">{fmtNum(j.current_step_no)}</span>.{" "}
                          <bdi>{j.current_step_title_ar}</bdi>
                        </>
                      ) : (
                        <Dash />
                      )}
                    </td>
                    <td className="t-desc">{j.current_dept_ar || <Dash />}</td>
                    <td className="t-desc">{j.still_label_ar || <Dash />}</td>
                    <td className="t-desc">
                      <span className="num">{fmtNum(j.summary.completed)}</span> من{" "}
                      <span className="num">{fmtNum(j.summary.total)}</span>
                    </td>
                    <td>
                      <span className="chip">
                        <span className="bullet" />
                        {j.status_label_ar}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {meta && meta.totalPages > 1 ? (
          <div className="reg-pager">
            <button
              type="button"
              className="btn sm ghost"
              disabled={page <= 1}
              onClick={() => setPage((n) => Math.max(1, n - 1))}
            >
              السابق
            </button>
            <span className="t-meta">
              صفحة <span className="num">{fmtNum(meta.page)}</span> من{" "}
              <span className="num">{fmtNum(meta.totalPages)}</span>
            </span>
            <button
              type="button"
              className="btn sm ghost"
              disabled={page >= meta.totalPages}
              onClick={() => setPage((n) => n + 1)}
            >
              التالي
            </button>
          </div>
        ) : null}
      </div>
    </Shell>
  );
}

/* ══════════════════════════ ملف الرحلة ══════════════════════════ */

function JourneyFile({ journeyId }: { journeyId: string }) {
  const { navigate } = useRouter();
  const [journey, setJourney] = useState<JourneyRow | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [toastAr, setToastAr] = useState<string | null>(null);
  /** الخطوة اللي لوحة إقفالها مفتوحة + سطر الدليل — واحدة بس في كل وقت */
  const [closing, setClosing] = useState<number | null>(null);
  const [note, setNote] = useState("");
  /** لوحة «أوقف الرحلة» + سبب الإيقاف */
  const [pausing, setPausing] = useState(false);
  const [pauseReason, setPauseReason] = useState("");

  const load = useCallback(async () => {
    setErrorAr(null);
    try {
      setJourney(await getJourney(journeyId));
    } catch (err) {
      console.error(`[journeys] تحميل الرحلة ${journeyId} فشل`, err);
      setErrorAr(errorMessageAr(err));
    }
  }, [journeyId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onNudge(stepNo: number) {
    if (busy !== null) return;
    setBusy(stepNo);
    try {
      setToastAr(await nudgeStep(journeyId, stepNo));
      await load();
    } catch (err) {
      console.error(`[journeys] التذكير على خطوة ${stepNo} فشل`, err);
      setErrorAr(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  /**
   * 🔑 بلاغ الفريق: «بضغط فتح الرحلة بيظهرلي إني أذكّر الفريق، ومش سيبني أكتب
   *    إنه تم تواصل». الإقفال كان موجود في السيستم ومش موجود في الشاشة.
   * ⛔ مش «تم» عمياء: سطر يقول اتعمل إيه، والسيرفر بيفرض شرط الخطوة كمان
   *    (الناقص بيتعرض فوق الزرار قبل ما تحاول).
   */
  async function onClose(stepNo: number) {
    if (busy !== null) return;
    const line = note.trim();
    if (line.length < 2) {
      setErrorAr("اكتب سطر يقول اتعمل إيه قبل ما تقفل.");
      return;
    }
    setErrorAr(null);
    setBusy(stepNo);
    try {
      setToastAr(await completeStep(journeyId, stepNo, line));
      setClosing(null);
      setNote("");
      await load();
    } catch (err) {
      console.error(`[journeys] إقفال خطوة ${stepNo} فشل`, err);
      setErrorAr(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  /** 🔑 بلاغ الفريق: «أسرة رفاعي لم يكملوا — عايزة أوقفها وأكتب السبب» ومفيش مكان. */
  async function onPause() {
    if (busy !== null) return;
    const reason = pauseReason.trim();
    if (reason.length < 2) {
      setErrorAr("اكتب سبب الإيقاف الأول.");
      return;
    }
    setErrorAr(null);
    setBusy(-1);
    try {
      setToastAr(await pauseJourney(journeyId, reason));
      setPausing(false);
      setPauseReason("");
      await load();
    } catch (err) {
      console.error("[journeys] إيقاف الرحلة فشل", err);
      setErrorAr(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  async function onResume() {
    if (busy !== null) return;
    setErrorAr(null);
    setBusy(-1);
    try {
      setToastAr(await resumeJourney(journeyId));
      await load();
    } catch (err) {
      console.error("[journeys] تكملة الرحلة فشلت", err);
      setErrorAr(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  if (errorAr && !journey) {
    return (
      <Shell title="الرحلة" crumb="رجوع">
        <ErrorState messageAr={errorAr} onRetry={() => void load()} />
      </Shell>
    );
  }
  if (!journey) {
    return (
      <Shell title="الرحلة">
        <LoadingState rows={4} />
      </Shell>
    );
  }

  return (
    <Shell title="الرحلة" crumb={journey.subject_label_ar}>
      <div className="reg-card">
        <div className="reg-head">
          <span className="sec-title">
            <bdi>{journey.subject_label_ar}</bdi> — {journey.template_label_ar}
          </span>
          <span className="t-meta">
            <span className="num">{fmtNum(journey.summary.completed)}</span> من{" "}
            <span className="num">{fmtNum(journey.summary.total)}</span> خطوة ·{" "}
            {journey.status_label_ar}
            {journey.still_label_ar ? ` · ${journey.still_label_ar}` : ""}
          </span>
        </div>

        {journey.status === "active" || journey.status === "waiting" || journey.status === "on_hold" ? (
          <div className="t-meta" style={{ padding: "0 14px 8px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {journey.status === "on_hold" ? (
              <>
                <span>الرحلة موقوفة — مفيش مهام مفتوحة عليها.</span>
                <button type="button" className={`btn sm${busy === -1 ? " is-busy" : ""}`}
                        disabled={busy !== null} onClick={() => void onResume()}>
                  كمّل الرحلة
                </button>
              </>
            ) : pausing ? (
              <>
                <input
                  type="text"
                  value={pauseReason}
                  placeholder="سبب الإيقاف (مثلًا: الأسرة ما كمّلتش التسجيل)"
                  aria-label="سبب إيقاف الرحلة"
                  onChange={(e) => setPauseReason(e.target.value)}
                  style={{ flex: 1, minWidth: 220, font: "inherit", color: "var(--ink)", background: "var(--surface)",
                           border: "1px solid var(--line)", borderRadius: "var(--r-control)", padding: "6px 10px" }}
                />
                <button type="button" className={`btn sm primary${busy === -1 ? " is-busy" : ""}`}
                        disabled={busy !== null} onClick={() => void onPause()}>
                  أوقف الرحلة
                </button>
                <button type="button" className="btn sm ghost" onClick={() => { setPausing(false); setPauseReason(""); }}>
                  إلغاء
                </button>
              </>
            ) : (
              <button type="button" className="btn sm ghost" onClick={() => setPausing(true)}>
                أوقف الرحلة
              </button>
            )}
          </div>
        ) : null}

        {toastAr ? (
          <div className="t-meta" role="status" style={{ padding: "0 14px 8px" }}>
            {toastAr}
          </div>
        ) : null}
        {errorAr ? (
          <div
            className="t-meta"
            role="alert"
            style={{ padding: "0 14px 8px", color: "var(--danger)" }}
          >
            {errorAr}
          </div>
        ) : null}

        <ol className="jrny-steps">
          {(journey.steps ?? []).map((s) => {
            const open = s.state === "ready" || s.state === "in_progress";
            return (
              <li key={s.step_no} className={`jrny-step is-${s.state}`}>
                <div className="jrny-step-head">
                  <span className="num">{fmtNum(s.step_no)}</span>
                  <bdi className="jrny-step-title">{s.title_ar}</bdi>
                  <span className="chip">
                    <span className="bullet" />
                    {s.state_label_ar}
                  </span>
                </div>
                <div className="t-meta">
                  {s.auto ? "تلقائية" : s.owner_dept_ar || "بلا قسم"}
                  {s.assignee ? ` · ${s.assignee.name_ar}` : ""}
                  {s.sla_label_ar ? ` · ${s.sla_label_ar}` : ""}
                </div>
                {/* 🔑 **الناقص بالعربي زي ما المحرّك بيرجّعه** — من غير ده
                    الموظفة بتشوف «مش قادرة تقفل» بلا سبب. */}
                {s.missing_ar && open ? (
                  <div className="t-meta jrny-missing">
                    {s.missing_ar}
                    {/* 🔑 بلاغ الفريق: «مش بيسمحلي أملي بيانات» — الشاشة كانت بتقول
                        الناقص ومش بتقول يتملى منين. الرابط بيودّي لكارت الاستكمال. */}
                    {journey.subject_href && s.action === "close" ? (
                      <>
                        {" · "}
                        <a
                          href={journey.subject_href}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(journey.subject_href!);
                          }}
                        >
                          كمّل الناقص من السجل
                        </a>
                      </>
                    ) : null}
                  </div>
                ) : null}
                {/* 🔑 فعل واحد لكل خطوة — السيرفر هو اللي قرّره (`action`)،
                    مش الشاشة. الخطوات المستنية بلا زرار: تذكيرها كان بيروح
                    لنفس اللي ماسك الخطوة المفتوحة. */}
                {s.action === "nudge" ? (
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={busy !== null}
                    onClick={() => void onNudge(s.step_no)}
                  >
                    {busy === s.step_no ? "بيتبعت…" : "ذكّر القسم"}
                  </button>
                ) : null}
                {s.action === "close" && closing !== s.step_no ? (
                  <button
                    type="button"
                    className="btn sm primary"
                    disabled={busy !== null}
                    onClick={() => {
                      setErrorAr(null);
                      setNote("");
                      setClosing(s.step_no);
                    }}
                  >
                    اقفل الخطوة
                  </button>
                ) : null}
                {s.action === "close" && closing === s.step_no ? (
                  <div className="jrny-close">
                    <input
                      className="reg-input"
                      value={note}
                      autoFocus
                      maxLength={500}
                      placeholder="اكتب سطر يقول اتعمل إيه — مثلًا: تم التواصل واتفقنا على موعد"
                      onChange={(e) => setNote(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void onClose(s.step_no);
                      }}
                    />
                    <div className="jrny-close-actions">
                      <button
                        type="button"
                        className="btn sm primary"
                        disabled={busy !== null || note.trim().length < 2}
                        onClick={() => void onClose(s.step_no)}
                      >
                        {busy === s.step_no ? "بيتقفل…" : "تأكيد الإقفال"}
                      </button>
                      <button
                        type="button"
                        className="btn sm ghost"
                        disabled={busy !== null}
                        onClick={() => setClosing(null)}
                      >
                        إلغاء
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>

        <div style={{ padding: 14 }}>
          <button type="button" className="btn ghost" onClick={() => navigate("/journeys")}>
            رجوع للقايمة
          </button>
        </div>
      </div>
    </Shell>
  );
}
